import React, { useState, useEffect, useCallback, useContext, useMemo } from "react";
import moment from "moment";
import { useHistory } from "react-router-dom";
import { makeStyles, useTheme, alpha, lighten } from "@material-ui/core/styles";
import {
  Typography,
  TextField,
  MenuItem,
  IconButton,
  CircularProgress,
  Button,
  Chip,
  Tooltip,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from "@material-ui/core";
import RefreshIcon from "@material-ui/icons/Refresh";
import EventNoteIcon from "@material-ui/icons/EventNote";
import AccessTimeIcon from "@material-ui/icons/AccessTime";

import MainContainer from "../../components/MainContainer";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import sidebarScrollbar from "../../helpers/sidebarScrollbar";

const DATE_FMT = "YYYY-MM-DD";

const PERIODS = [
  { key: "today", label: "Hoje" },
  { key: "7", label: "7 dias" },
  { key: "30", label: "30 dias" },
  { key: "month", label: "Este mês" },
  { key: "custom", label: "Personalizado" },
];

// ordem de empilhamento no gráfico (de cima para baixo)
const CHART_ORDER = ["aRealizar", "semDesfecho", "cancelado", "faltou", "concluido"];
const LEGEND_ORDER = ["concluido", "faltou", "cancelado", "semDesfecho", "aRealizar"];

const formatBRL = (value) =>
  Number(value || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const formatBRLShort = (value) =>
  Number(value || 0).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
    maximumFractionDigits: 0,
  });

const initialsOf = (name = "") => {
  const parts = name.replace(/[^A-Za-zÀ-ÿ\s]/g, " ").trim().split(/\s+/);
  return ((parts[0] || "?")[0] + (parts[1] ? parts[1][0] : "")).toUpperCase();
};

const useStyles = makeStyles((theme) => ({
  scroll: {
    color: theme.palette.text.primary,
    flex: 1,
    overflowY: "auto",
    padding: theme.spacing(0, 0.75, 3),
    ...sidebarScrollbar(theme),
  },
  header: {
    color: theme.palette.text.primary,
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    flexWrap: "wrap",
    padding: theme.spacing(0, 0.75, 2),
  },
  title: { fontSize: 26, fontWeight: 700, lineHeight: 1.2, [theme.breakpoints.down("xs")]: { fontSize: 22 } },
  muted: { color: theme.palette.text.secondary, fontSize: 13 },
  filters: { display: "flex", alignItems: "center", gap: theme.spacing(1), flexWrap: "wrap" },
  periodScroll: {
    display: "flex",
    gap: theme.spacing(1),
    [theme.breakpoints.down("xs")]: { width: "100%", overflowX: "auto", paddingBottom: 2 },
  },
  periodChip: { fontWeight: 500, height: 32, borderRadius: 16, flexShrink: 0 },
  select: {
    minWidth: 190,
    "& .MuiOutlinedInput-root": {
      borderRadius: 10,
      height: 34,
      fontSize: 13,
      backgroundColor: theme.palette.background.paper,
    },
    [theme.breakpoints.down("xs")]: { flex: 1, "& .MuiOutlinedInput-root": { height: 44 } },
  },
  dateField: {
    width: 150,
    "& .MuiOutlinedInput-root": {
      borderRadius: 10,
      height: 34,
      fontSize: 13,
      backgroundColor: theme.palette.background.paper,
    },
  },
  button: {
    textTransform: "none",
    fontWeight: 600,
    borderRadius: 10,
    height: 34,
    color: theme.palette.text.primary,
    borderColor: theme.palette.divider,
    backgroundColor: theme.palette.background.paper,
  },
  card: {
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 14,
  },
  alert: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: theme.spacing(1.75),
    padding: theme.spacing(1.75, 2.25),
    borderRadius: 14,
    marginBottom: theme.spacing(2),
  },
  alertIcon: {
    width: 36,
    height: 36,
    borderRadius: 10,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  kpis: {
    display: "grid",
    gridTemplateColumns: "repeat(5, minmax(0, 1fr))",
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down("md")]: { gridTemplateColumns: "repeat(3, minmax(0, 1fr))" },
    [theme.breakpoints.down("xs")]: { gridTemplateColumns: "repeat(2, minmax(0, 1fr))" },
  },
  kpi: { display: "flex", flexDirection: "column", gap: 4, padding: theme.spacing(2, 2.25) },
  kpiFirst: {
    [theme.breakpoints.down("xs")]: { gridColumn: "span 2" },
  },
  kpiLabel: {
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: "0.05em",
    textTransform: "uppercase",
    color: theme.palette.text.secondary,
  },
  kpiValue: { fontSize: 24, fontWeight: 700, lineHeight: 1.25, [theme.breakpoints.down("xs")]: { fontSize: 20 } },
  kpiDelta: { fontSize: 12, fontWeight: 600 },
  grid: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1.6fr) minmax(0, 1fr)",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down("sm")]: { gridTemplateColumns: "minmax(0, 1fr)" },
  },
  grid2: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: theme.spacing(2),
    [theme.breakpoints.down("sm")]: { gridTemplateColumns: "minmax(0, 1fr)" },
  },
  section: { display: "flex", flexDirection: "column", gap: theme.spacing(1.75), padding: theme.spacing(2.5, 2.75), [theme.breakpoints.down("xs")]: { padding: theme.spacing(2) } },
  sectionTitle: { fontSize: 16, fontWeight: 700 },
  legend: { display: "flex", flexWrap: "wrap", gap: theme.spacing(1.5) },
  legendItem: { display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, color: theme.palette.text.secondary },
  legendDot: { width: 10, height: 10, borderRadius: 3 },
  chartWrap: { display: "flex", gap: 10 },
  yAxis: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    fontSize: 11,
    color: theme.palette.text.secondary,
    textAlign: "right",
    width: 22,
    flexShrink: 0,
  },
  plot: { position: "relative", height: 192 },
  gridLine: { position: "absolute", left: 0, right: 0, borderTop: `1px dashed ${theme.palette.divider}` },
  bars: { position: "absolute", inset: 0, display: "flex", alignItems: "flex-end" },
  barCol: { flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "flex-end", height: "100%", minWidth: 0 },
  barStack: { width: "70%", maxWidth: 40, display: "flex", flexDirection: "column" },
  barTotal: { fontSize: 11, fontWeight: 600, color: theme.palette.text.secondary, marginBottom: 3 },
  xAxis: { display: "flex", marginTop: 6 },
  xLabel: { flex: 1, textAlign: "center", fontSize: 11, lineHeight: 1.3, whiteSpace: "pre-line", minWidth: 0 },
  groupLabel: {
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: "0.05em",
    textTransform: "uppercase",
    color: theme.palette.text.secondary,
    padding: theme.spacing(0.75, 0, 0.5),
  },
  upRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.1, 1),
    margin: theme.spacing(0, -1),
    borderRadius: 10,
    cursor: "pointer",
    color: "inherit",
    background: "none",
    border: "none",
    borderBottom: `1px solid ${theme.palette.divider}`,
    width: `calc(100% + ${theme.spacing(2)}px)`,
    textAlign: "left",
    font: "inherit",
    minHeight: 48,
    "&:hover": { backgroundColor: alpha(theme.palette.primary.main, 0.05) },
  },
  upTime: { fontWeight: 700, fontSize: 14, minWidth: 44, color: theme.palette.type === "dark" ? "#7FB2F5" : theme.palette.primary.dark },
  ellipsis: { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  pill: { fontSize: 12, fontWeight: 600, padding: "3px 10px", borderRadius: 999, flexShrink: 0 },
  headCell: {
    color: theme.palette.text.secondary,
    fontSize: 12,
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    whiteSpace: "nowrap",
  },
  track: { height: 8, borderRadius: 999, backgroundColor: theme.palette.action.hover, overflow: "hidden", flex: 1 },
  fill: { height: "100%", borderRadius: 999 },
  avatar: {
    width: 28,
    height: 28,
    borderRadius: "50%",
    fontSize: 12,
    fontWeight: 700,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: alpha(theme.palette.primary.main, 0.18),
    color: theme.palette.type === "dark" ? "#CFE0FA" : theme.palette.primary.dark,
    flexShrink: 0,
  },
  desktopOnly: { [theme.breakpoints.down("xs")]: { display: "none" } },
  mobileOnly: { display: "none", [theme.breakpoints.down("xs")]: { display: "block" } },
  mCard: { display: "flex", flexDirection: "column", gap: 10, padding: theme.spacing(1.5), border: `1px solid ${theme.palette.divider}`, borderRadius: 12 },
  centered: { display: "flex", justifyContent: "center", padding: theme.spacing(6) },
  empty: { color: theme.palette.text.secondary, fontSize: 14 },
}));

const AppointmentDashboard = () => {
  const classes = useStyles();
  const theme = useTheme();
  const history = useHistory();
  const { user } = useContext(AuthContext);
  const isAdmin = user.profile === "admin";
  const dark = theme.palette.type === "dark";

  const tone = (c) => (dark ? lighten(c, 0.3) : c);
  const GREEN = tone("#1E8449");
  const RED = tone("#C93035");
  const AMBER_TEXT = dark ? "#F5B04A" : "#A04A07";
  const LINK = dark ? "#7FB2F5" : theme.palette.primary.dark;

  const STATUS = {
    concluido: { label: "Concluído", color: dark ? "#3FB56A" : "#2E9E5B" },
    faltou: { label: "Faltou", color: dark ? "#F06A6E" : "#E5484D" },
    cancelado: { label: "Cancelado", color: dark ? "#6B7385" : "#B4BAC3" },
    semDesfecho: { label: "Sem desfecho", color: "#F5A524" },
    aRealizar: { label: "A realizar", color: "#2D7BE8" },
  };

  const [periodKey, setPeriodKey] = useState("7");
  const [dateFrom, setDateFrom] = useState(moment().subtract(6, "days").format(DATE_FMT));
  const [dateTo, setDateTo] = useState(moment().format(DATE_FMT));
  const [providerId, setProviderId] = useState("");
  const [providerList, setProviderList] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isAdmin) return;
    api
      .get("/users/list")
      .then(({ data: res }) => setProviderList(Array.isArray(res) ? res : res?.users || []))
      .catch(toastError);
  }, [isAdmin]);

  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const { data: res } = await api.get("/appointments/dashboard", {
        params: {
          period: periodKey,
          dateFrom: periodKey === "custom" ? dateFrom : undefined,
          dateTo: periodKey === "custom" ? dateTo : undefined,
          userId: providerId || undefined,
        },
      });
      setData(res);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, [periodKey, dateFrom, dateTo, providerId]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  const cards = data?.cards;
  const prev = data?.previous;

  const rangeLabel = data
    ? `${moment(data.range.from).format("DD/MM/YYYY")} a ${moment(data.range.to).format("DD/MM/YYYY")}`
    : "";
  const providerName = providerList.find((u) => String(u.id) === String(providerId))?.name;
  const subtitle = `${rangeLabel} · ${
    !isAdmin ? "seus agendamentos" : providerName || "todos os profissionais"
  }`;

  const delta = (current, previous, { money = false, points = false, invert = false } = {}) => {
    if (previous == null || current == null) return null;
    const diff = current - previous;
    if (diff === 0) return { text: "igual ao período anterior", color: theme.palette.text.secondary };
    const good = invert ? diff < 0 : diff > 0;
    const arrow = diff > 0 ? "▲" : "▼";
    let text;
    if (points) text = `${arrow} ${Math.abs(diff)} p.p.`;
    else if (money) text = `${arrow} ${formatBRLShort(Math.abs(diff))}`;
    else if (previous > 0) text = `${arrow} ${Math.round((Math.abs(diff) / previous) * 100)}%`;
    else text = `${arrow} ${Math.abs(diff)}`;
    return { text: `${text} vs. período anterior`, color: good ? GREEN : RED };
  };

  const kpis = cards
    ? [
        {
          label: "Agendamentos",
          value: String(cards.total),
          caption: `${cards.concluido} concluídos · ${cards.faltou} faltas · ${cards.cancelado} cancelados`,
          delta: delta(cards.total, prev?.total),
        },
        {
          label: "Receita realizada",
          value: formatBRLShort(cards.revenueRealized),
          color: GREEN,
          caption: `${cards.concluido} ${cards.concluido === 1 ? "atendimento concluído" : "atendimentos concluídos"}`,
          delta: delta(cards.revenueRealized, prev?.revenueRealized, { money: true }),
        },
        {
          label: "Receita prevista",
          value: formatBRLShort(cards.revenueForecast),
          caption: `${cards.aRealizar + cards.semDesfecho} a realizar ou sem desfecho`,
          delta: { text: "inclui pendentes e confirmados", color: theme.palette.text.secondary },
        },
        {
          label: "Comparecimento",
          value: cards.attendanceRate != null ? `${cards.attendanceRate}%` : "—",
          caption: "concluídos ÷ (concluídos + faltas)",
          delta: delta(cards.attendanceRate, prev?.attendanceRate, { points: true }),
        },
        {
          label: "Ticket médio",
          value: cards.avgTicket != null ? formatBRLShort(cards.avgTicket) : "—",
          caption: "por atendimento concluído",
          delta: delta(cards.avgTicket, prev?.avgTicket),
        },
      ]
    : [];

  const chart = data?.chart || [];
  const chartMax = useMemo(() => {
    const totals = chart.map((d) => CHART_ORDER.reduce((n, k) => n + (d[k] || 0), 0));
    const max = Math.max(4, ...totals);
    return Math.ceil(max / 3) * 3;
  }, [chart]);
  const todayStr = moment().format(DATE_FMT);
  const dense = chart.length > 14;

  const legendCounts = cards
    ? {
        concluido: cards.concluido,
        faltou: cards.faltou,
        cancelado: cards.cancelado,
        semDesfecho: cards.semDesfecho,
        aRealizar: cards.aRealizar,
      }
    : {};

  const upcomingGroups = useMemo(() => {
    const groups = [];
    (data?.upcoming || []).forEach((appt) => {
      const day = moment(appt.startAt);
      const diff = day.clone().startOf("day").diff(moment().startOf("day"), "days");
      const label =
        (diff === 0 ? "Hoje · " : diff === 1 ? "Amanhã · " : "") + day.format("ddd DD/MM");
      let g = groups.find((x) => x.label === label);
      if (!g) {
        g = { label, items: [] };
        groups.push(g);
      }
      g.items.push(appt);
    });
    return groups;
  }, [data]);

  const pillStyle = (status) => {
    const confirmed = status === "confirmado";
    return {
      color: confirmed ? LINK : AMBER_TEXT,
      backgroundColor: confirmed ? alpha("#2D7BE8", dark ? 0.2 : 0.12) : alpha("#F5A524", dark ? 0.2 : 0.18),
    };
  };

  const services = data?.services || [];
  const providers = data?.providers || [];
  const totalForShare = cards?.total || 1;
  const showProviders = isAdmin && !providerId && providers.length > 0;

  return (
    <MainContainer>
      <div className={classes.header}>
        <div>
          <Typography component="h1" className={classes.title}>
            Dashboard de agendamentos
          </Typography>
          <Typography className={classes.muted}>{subtitle}</Typography>
        </div>
        <div className={classes.filters}>
          <div className={classes.periodScroll}>
            {PERIODS.map((p) => (
              <Chip
                key={p.key}
                clickable
                className={classes.periodChip}
                label={p.label}
                color={periodKey === p.key ? "primary" : "default"}
                variant={periodKey === p.key ? "default" : "outlined"}
                onClick={() => setPeriodKey(p.key)}
                aria-pressed={periodKey === p.key}
              />
            ))}
          </div>
          {periodKey === "custom" && (
            <>
              <TextField
                type="date"
                size="small"
                variant="outlined"
                className={classes.dateField}
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                inputProps={{ "aria-label": "De" }}
              />
              <TextField
                type="date"
                size="small"
                variant="outlined"
                className={classes.dateField}
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                inputProps={{ "aria-label": "Até" }}
              />
            </>
          )}
          {isAdmin && (
            <TextField
              select
              size="small"
              variant="outlined"
              className={classes.select}
              value={providerId}
              onChange={(e) => setProviderId(e.target.value)}
              SelectProps={{ displayEmpty: true }}
              inputProps={{ "aria-label": "Profissional" }}
            >
              <MenuItem value="">Todos os profissionais</MenuItem>
              {providerList.map((u) => (
                <MenuItem key={u.id} value={u.id}>
                  {u.name}
                </MenuItem>
              ))}
            </TextField>
          )}
          <Button
            variant="outlined"
            className={classes.button}
            startIcon={<EventNoteIcon />}
            onClick={() => history.push("/appointments")}
          >
            Abrir agenda
          </Button>
          <Tooltip title="Atualizar">
            <span>
              <IconButton size="small" onClick={fetchDashboard} disabled={loading} aria-label="Atualizar">
                <RefreshIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </div>
      </div>

      <div className={classes.scroll}>
        {loading && !data ? (
          <div className={classes.centered}>
            <CircularProgress />
          </div>
        ) : !data ? null : (
          <div style={{ opacity: loading ? 0.6 : 1, transition: "opacity .15s" }}>
            {data.semDesfechoTotal > 0 && (
              <section
                className={classes.alert}
                style={{
                  backgroundColor: alpha("#F5A524", dark ? 0.12 : 0.1),
                  border: `1px solid ${alpha("#F5A524", dark ? 0.35 : 0.45)}`,
                }}
              >
                <span className={classes.alertIcon} style={{ backgroundColor: alpha("#F5A524", 0.2), color: AMBER_TEXT }}>
                  <AccessTimeIcon fontSize="small" />
                </span>
                <div style={{ flex: "1 1 320px", minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>
                    {data.semDesfechoTotal}{" "}
                    {data.semDesfechoTotal === 1
                      ? "agendamento já passou e continua sem desfecho"
                      : "agendamentos já passaram e continuam sem desfecho"}
                  </div>
                  <div className={classes.muted} style={{ marginTop: 2 }}>
                    Marque cada um como concluído ou faltou para que a receita e o comparecimento fiquem
                    corretos.
                  </div>
                </div>
                <Button
                  variant="outlined"
                  className={classes.button}
                  style={{ color: AMBER_TEXT, borderColor: alpha("#F5A524", 0.6) }}
                  onClick={() => history.push("/appointments")}
                >
                  Revisar agora
                </Button>
              </section>
            )}

            <div className={classes.kpis}>
              {kpis.map((kpi, i) => (
                <div key={kpi.label} className={`${classes.card} ${classes.kpi} ${i === 0 ? classes.kpiFirst : ""}`}>
                  <span className={classes.kpiLabel}>{kpi.label}</span>
                  <span className={classes.kpiValue} style={kpi.color ? { color: kpi.color } : undefined}>
                    {kpi.value}
                  </span>
                  <span className={`${classes.muted} ${i === 0 ? "" : classes.desktopOnly}`} style={{ fontSize: 12 }}>
                    {kpi.caption}
                  </span>
                  {kpi.delta && (
                    <span className={classes.kpiDelta} style={{ color: kpi.delta.color }}>
                      {kpi.delta.text}
                    </span>
                  )}
                </div>
              ))}
            </div>

            <div className={classes.grid}>
              <section className={`${classes.card} ${classes.section}`}>
                <div>
                  <Typography component="h2" className={classes.sectionTitle}>
                    Volume por dia
                  </Typography>
                  <Typography className={classes.muted}>Situação de cada agendamento do período.</Typography>
                </div>
                <div className={classes.legend}>
                  {LEGEND_ORDER.map((k) => (
                    <span key={k} className={classes.legendItem}>
                      <span className={classes.legendDot} style={{ backgroundColor: STATUS[k].color }} />
                      {STATUS[k].label}{" "}
                      <strong style={{ color: theme.palette.text.primary }}>{legendCounts[k] || 0}</strong>
                    </span>
                  ))}
                </div>
                {chart.every((d) => CHART_ORDER.every((k) => !d[k])) ? (
                  <Typography className={classes.empty}>Nenhum agendamento no período.</Typography>
                ) : (
                  <div className={classes.chartWrap}>
                    <div className={classes.yAxis} style={{ height: 192 }} aria-hidden="true">
                      {[chartMax, (chartMax * 2) / 3, chartMax / 3, 0].map((v, i) => (
                        <span key={i}>{v}</span>
                      ))}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className={classes.plot}>
                        {[0, 33.33, 66.66].map((t) => (
                          <div key={t} className={classes.gridLine} style={{ top: `${t}%` }} />
                        ))}
                        <div className={classes.gridLine} style={{ bottom: 0, borderTopStyle: "solid" }} />
                        <div className={classes.bars}>
                          {chart.map((d) => {
                            const total = CHART_ORDER.reduce((n, k) => n + (d[k] || 0), 0);
                            let first = true;
                            return (
                              <div
                                key={d.date}
                                className={classes.barCol}
                                title={`${d.label}: ${total} ${total === 1 ? "agendamento" : "agendamentos"}`}
                              >
                                {!dense && <span className={classes.barTotal}>{total || "—"}</span>}
                                <div className={classes.barStack} style={dense ? { width: "80%" } : undefined}>
                                  {CHART_ORDER.map((k) => {
                                    const n = d[k] || 0;
                                    if (!n) return null;
                                    const top = first;
                                    first = false;
                                    return (
                                      <div
                                        key={k}
                                        style={{
                                          height: `${(n / chartMax) * 192}px`,
                                          backgroundColor: STATUS[k].color,
                                          borderRadius: top ? "5px 5px 0 0" : 0,
                                          borderTop: top ? "none" : `1px solid ${theme.palette.background.paper}`,
                                          boxSizing: "border-box",
                                        }}
                                      />
                                    );
                                  })}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                      <div className={classes.xAxis}>
                        {chart.map((d, i) => {
                          const isToday = d.date === todayStr;
                          const showLabel = !dense || i % Math.ceil(chart.length / 8) === 0 || isToday;
                          return (
                            <span
                              key={d.date}
                              className={classes.xLabel}
                              style={{
                                color: isToday ? theme.palette.text.primary : theme.palette.text.secondary,
                                fontWeight: isToday ? 700 : 500,
                              }}
                            >
                              {showLabel ? (dense ? d.label : `${isToday ? "Hoje" : moment(d.date).format("ddd")}\n${d.label.slice(0, 2)}`) : ""}
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}
              </section>

              <section className={`${classes.card} ${classes.section}`} style={{ gap: 8 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 12 }}>
                  <Typography component="h2" className={classes.sectionTitle}>
                    Próximos agendamentos
                  </Typography>
                  <Button size="small" style={{ textTransform: "none", fontWeight: 600, color: LINK }} onClick={() => history.push("/appointments")}>
                    Ver agenda
                  </Button>
                </div>
                {upcomingGroups.length === 0 ? (
                  <Typography className={classes.empty}>Nenhum agendamento futuro.</Typography>
                ) : (
                  upcomingGroups.map((g) => (
                    <div key={g.label}>
                      <div className={classes.groupLabel}>{g.label}</div>
                      {g.items.map((appt) => (
                        <button
                          type="button"
                          key={appt.id}
                          className={classes.upRow}
                          onClick={() => history.push("/appointments")}
                        >
                          <span className={classes.upTime}>{moment(appt.startAt).format("HH:mm")}</span>
                          <span style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
                            <span className={classes.ellipsis} style={{ fontWeight: 600, fontSize: 14 }}>
                              {appt.contact?.name || "—"}
                            </span>
                            <span className={`${classes.muted} ${classes.ellipsis}`} style={{ fontSize: 12 }}>
                              {appt.service?.name}
                              {isAdmin && appt.user ? ` · ${appt.user.name}` : ""}
                            </span>
                          </span>
                          <span className={classes.pill} style={pillStyle(appt.status)}>
                            {appt.status === "confirmado" ? "Confirmado" : "Pendente"}
                          </span>
                        </button>
                      ))}
                    </div>
                  ))
                )}
              </section>
            </div>

            <div className={classes.grid2}>
              <section className={`${classes.card} ${classes.section}`}>
                <div>
                  <Typography component="h2" className={classes.sectionTitle}>
                    Serviços
                  </Typography>
                  <Typography className={classes.muted}>Mais agendados no período e quanto renderam.</Typography>
                </div>
                {services.length === 0 ? (
                  <Typography className={classes.empty}>Sem dados no período.</Typography>
                ) : (
                  <>
                    <div className={classes.desktopOnly} style={{ overflowX: "auto" }}>
                      <Table size="small" style={{ minWidth: 380 }}>
                        <TableHead>
                          <TableRow>
                            <TableCell className={classes.headCell}>Serviço</TableCell>
                            <TableCell className={classes.headCell} align="right">Agend.</TableCell>
                            <TableCell className={classes.headCell} style={{ width: "34%" }}>Participação</TableCell>
                            <TableCell className={classes.headCell} align="right">Receita</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {services.map((s) => {
                            const pct = Math.round((s.count / totalForShare) * 100);
                            return (
                              <TableRow key={s.id}>
                                <TableCell style={{ fontWeight: 600 }}>{s.name}</TableCell>
                                <TableCell align="right">{s.count}</TableCell>
                                <TableCell>
                                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                    <div className={classes.track}>
                                      <div className={classes.fill} style={{ width: `${pct}%`, backgroundColor: theme.palette.primary.main }} />
                                    </div>
                                    <span className={classes.muted} style={{ width: 34, textAlign: "right", fontSize: 12 }}>{pct}%</span>
                                  </div>
                                </TableCell>
                                <TableCell align="right" style={{ fontWeight: 600 }}>{formatBRLShort(s.revenue)}</TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </div>
                    <div className={classes.mobileOnly}>
                      {services.map((s) => {
                        const pct = Math.round((s.count / totalForShare) * 100);
                        return (
                          <div key={s.id} style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 14 }}>
                            <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                              <span style={{ fontWeight: 600 }}>{s.name}</span>
                              <span className={classes.muted} style={{ whiteSpace: "nowrap" }}>
                                <strong style={{ color: theme.palette.text.primary }}>{s.count}</strong> · {formatBRLShort(s.revenue)}
                              </span>
                            </div>
                            <div className={classes.track}>
                              <div className={classes.fill} style={{ width: `${pct}%`, backgroundColor: theme.palette.primary.main }} />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
              </section>

              {showProviders && (
                <section className={`${classes.card} ${classes.section}`}>
                  <div>
                    <Typography component="h2" className={classes.sectionTitle}>
                      Por profissional
                    </Typography>
                    <Typography className={classes.muted}>
                      Agendamentos, comparecimento e receita de cada um.
                    </Typography>
                  </div>
                  <div className={classes.desktopOnly} style={{ overflowX: "auto" }}>
                    <Table size="small" style={{ minWidth: 420 }}>
                      <TableHead>
                        <TableRow>
                          <TableCell className={classes.headCell}>Profissional</TableCell>
                          <TableCell className={classes.headCell} align="right">Agend.</TableCell>
                          <TableCell className={classes.headCell} style={{ width: "34%" }}>Comparecimento</TableCell>
                          <TableCell className={classes.headCell} align="right">Receita</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {providers.map((p) => (
                          <TableRow key={p.id}>
                            <TableCell>
                              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                <span className={classes.avatar}>{initialsOf(p.name)}</span>
                                <span style={{ fontWeight: 600 }}>{p.name}</span>
                              </div>
                            </TableCell>
                            <TableCell align="right">{p.count}</TableCell>
                            <TableCell>
                              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                <div className={classes.track}>
                                  <div className={classes.fill} style={{ width: `${p.attendanceRate || 0}%`, backgroundColor: STATUS.concluido.color }} />
                                </div>
                                <span className={classes.muted} style={{ width: 34, textAlign: "right", fontSize: 12 }}>
                                  {p.attendanceRate != null ? `${p.attendanceRate}%` : "—"}
                                </span>
                              </div>
                            </TableCell>
                            <TableCell align="right" style={{ fontWeight: 600 }}>{formatBRLShort(p.revenue)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                  <div className={classes.mobileOnly}>
                    {providers.map((p) => (
                      <div key={p.id} className={classes.mCard} style={{ marginBottom: 10 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                          <span className={classes.avatar} style={{ width: 30, height: 30 }}>{initialsOf(p.name)}</span>
                          <span style={{ fontWeight: 600, flex: 1 }}>{p.name}</span>
                          <span style={{ fontWeight: 700 }}>{formatBRLShort(p.revenue)}</span>
                        </div>
                        <div style={{ display: "flex", justifyContent: "space-between" }} className={classes.muted}>
                          <span>Agendamentos <strong style={{ color: theme.palette.text.primary }}>{p.count}</strong></span>
                          <span>Comparecimento <strong style={{ color: theme.palette.text.primary }}>{p.attendanceRate != null ? `${p.attendanceRate}%` : "—"}</strong></span>
                        </div>
                        <div className={classes.track} style={{ height: 6, flex: "none" }}>
                          <div className={classes.fill} style={{ width: `${p.attendanceRate || 0}%`, backgroundColor: STATUS.concluido.color }} />
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
              )}
            </div>
          </div>
        )}
      </div>
    </MainContainer>
  );
};

export default AppointmentDashboard;
